import React, { useState, useEffect } from 'react';
import { Package, Shield, Eye, EyeOff } from 'lucide-react';

interface PostalCodeData {
  postcode: string;
  city: string;
  state: string;
}

const AdminBundlePage: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  // Einfaches Passwort-System (in einer echten App würde man ein sichereres System verwenden)
  const ADMIN_PASSWORD = 'easy2025admin';

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setError('');
      localStorage.setItem('adminAuth', 'true');
    } else {
      setError('Falsches Passwort');
    }
  };

  useEffect(() => {
    // Prüfen ob bereits eingeloggt
    const savedAuth = localStorage.getItem('adminAuth');
    if (savedAuth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('adminAuth');
    setPassword('');
  };

  if (!isAuthenticated) {
    return (
      <div className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-md mx-auto">
            <div className="bg-white rounded-lg shadow-md p-8">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-red-600" />
                </div>
                <h1 className="text-2xl font-bold text-gray-800">Admin-Bereich</h1>
                <p className="text-gray-600 mt-2">Bitte geben Sie das Admin-Passwort ein</p>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              )}

              <form onSubmit={handleLogin}>
                <div className="mb-6">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Passwort
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors pr-10"
                      placeholder="Admin-Passwort eingeben"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full py-3 px-6 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-300"
                >
                  Anmelden
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-16">
      {/* Admin Header */}
      <section className="bg-gradient-to-r from-red-600 to-orange-600 text-white py-8">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold mb-2">Admin: Bundle-Vergleichsrechner</h1>
              <p className="text-red-100">Nur für interne Nutzung - nicht für Kunden sichtbar</p>
            </div>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-white bg-opacity-20 text-white rounded-lg hover:bg-opacity-30 transition-colors"
            >
              Abmelden
            </button>
          </div>
        </div>
      </section>

      {/* Bundle Calculator */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="p-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                <div className="flex items-center">
                  <Package className="w-8 h-8 mr-3" />
                  <div>
                    <h2 className="text-2xl font-bold mb-1">Bundle-Vergleichsrechner</h2>
                    <p>Smartphone + Tarif Kombinationen vergleichen</p>
                  </div>
                </div>
              </div>
              
              <div className="p-4">
                <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center">
                    <Shield className="w-5 h-5 text-yellow-600 mr-2" />
                    <p className="text-yellow-800 text-sm font-medium">
                      <strong>Admin-Hinweis:</strong> Dieser Rechner ist nur für Sie sichtbar und wird nicht in der öffentlichen Navigation angezeigt.
                    </p>
                  </div>
                </div>

                <iframe
                  src="https://tools.communicationads.net/calc.php?cl=bundle&wf=18676&country=DE"
                  className="w-full min-h-[1000px] border-0 rounded-lg"
                  title="Bundle-Vergleichsrechner (Admin)"
                  style={{
                    transform: 'scale(1)',
                    transformOrigin: 'top left'
                  }}
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Admin Info */}
      <section className="py-8 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Admin-Informationen</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-800 mb-2">Zugriff:</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Nur über direkte URL erreichbar</li>
                    <li>• Passwort-geschützt</li>
                    <li>• Nicht in der öffentlichen Navigation</li>
                    <li>• Session wird lokal gespeichert</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-gray-800 mb-2">Funktionen:</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Bundle-Vergleichsrechner</li>
                    <li>• Smartphone + Tarif Kombinationen</li>
                    <li>• Alle Anbieter verfügbar</li>
                    <li>• Vollständige Funktionalität</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdminBundlePage;